/**
 * AgentChatBubble - Minimal, high-signal AI interface
 *
 * Focuses on intelligence & context awareness without visual clutter.
 */

// Created: 2026-02-27 by Douglas + Claude
// Last edited: 2026-02-27 by Douglas — rewired to agent_commands pipeline (PromptAssembler on DO)

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, Send, Loader2, Minus, X, WifiOff, Activity, Lightbulb, Play, CheckCircle, Trash2, RotateCcw, Maximize2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModalFocus } from '../contexts/ModalFocusContext';
import { useScreenContext } from '../contexts/ScreenContextProvider';
import FeatureSuggestionPanel from '../lots/knowledge-base/vehicles/FeatureSuggestionPanel';
import screenAutomation from '../services/ScreenAutomationService';
import BrickToolbox from './ai/BrickToolbox';
import QAMessage from './ai/QAMessage';
import AgentThinkingAnimation from './ai/AgentThinkingAnimation';
import { supabase } from '@/lib/supabase';

/**
 * useAgentCommands — The pipeline hook.
 *
 * Instead of hitting a chat API, this:
 * 1. Builds a rich context object from screen + modal state
 * 2. INSERTs into agent_commands (picked up by avengers.js on DO)
 * 3. Subscribes via Realtime for the response
 * 4. PromptAssembler on DO compiles the perfect prompt from brick context
 *
 * The chat bubble is the UI. agent_commands is the pipeline. PromptAssembler is the brain.
 */
const useAgentCommands = ({ body, onFinish, onError, initialMessages = [] }) => {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingCommandId, setPendingCommandId] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const subscriptionRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleInputChange = (e) => setInput(e.target.value);

  const append = useCallback((message) => {
    setMessages(prev => [...prev, { ...message, id: Date.now().toString() }]);
  }, []);

  // Subscribe to response for a specific command
  const subscribeToResponse = useCallback((commandId) => {
    // Clean up any existing subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
    }

    // Listen on the broadcast channel for this command's response
    const channel = supabase.channel('agent-responses')
      .on('broadcast', { event: 'response' }, (payload) => {
        const data = payload.payload;
        if (data.command_id !== commandId) return;

        console.log('[AgentChat] Response received:', data.agent, data.status);

        const content = data.text || 'Done.';

        // Try to parse JSON response for structured data
        let parsed = {};
        try {
          parsed = typeof content === 'string' && content.startsWith('{')
            ? JSON.parse(content)
            : {};
        } catch { /* not JSON, that's fine */ }

        const assistantMessage = {
          role: 'assistant',
          content: parsed.summary || content,
          id: (Date.now() + 1).toString(),
          agent: { name: data.agent_name || data.agent },
          actions: parsed.actions,
          data: parsed.data,
          bricksUsed: parsed.bricks_used,
          isError: data.status === 'failed',
          // Q&A pattern — agent can include options for quick-reply chips
          options: parsed.options || null,
          isThinking: parsed.is_thinking || false,
          thinkingText: parsed.thinking_text || null,
          qaStep: parsed.qa_step || null,
          qaTotalSteps: parsed.qa_total_steps || null,
          qaCategory: parsed.qa_category || null,
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        setPendingCommandId(null);
        setPendingPrompt(null);

        if (onFinish) onFinish(assistantMessage);

        // Cleanup
        supabase.removeChannel(channel);
        subscriptionRef.current = null;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      })
      .subscribe();

    subscriptionRef.current = channel;

    // Timeout: if no response within 90s, fall back to polling
    timeoutRef.current = setTimeout(async () => {
      console.log('[AgentChat] Realtime timeout, polling for response...');
      const { data: cmd } = await supabase
        .from('agent_commands')
        .select('status, response_text, response_data, error_message')
        .eq('id', commandId)
        .single();

      if (cmd && (cmd.status === 'completed' || cmd.status === 'failed')) {
        let parsed = {};
        try {
          const text = cmd.response_text || '';
          parsed = text.startsWith('{') ? JSON.parse(text) : {};
        } catch { /* not JSON */ }

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: parsed.summary || cmd.response_text || cmd.error_message || 'Task completed.',
          id: (Date.now() + 1).toString(),
          isError: cmd.status === 'failed',
        }]);
        setIsLoading(false);
        setPendingCommandId(null);
        setPendingPrompt(null);
      }

      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    }, 90_000);
  }, [onFinish]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input, id: Date.now().toString() };
    setPendingPrompt(input);
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      // Build the context object — this IS the instance the DO side will use
      const context = {
        // Screen state
        currentLot: body.currentLot,
        currentRoute: body.currentRoute,
        platform: 'web',
        // Selected brick from BrickToolbox (user explicitly picked a domain)
        // This is PRIMARY — overrides modal-inferred brickHint
        selectedBrick: body.selectedBrick || null,
        // Modal focus (if open) — this makes the prompt hyper-specific
        modal: body.screenContext?.contextMode === 'modal_exclusive' ? {
          type: body.screenContext.modal?.type,
          entityType: body.screenContext.modal?.entityType,
          entityId: body.screenContext.modal?.entityId,
          entity: body.screenContext.modal?.entity,
          availableActions: body.screenContext.modal?.availableActions,
        } : null,
        // Brick hint: user-selected brick wins, then modal-inferred, then null
        brickHint: body.selectedBrick?.id
          || body.screenContext?.modal?.entity?.brick
          || body.screenContext?.relationships?.brick
          || null,
        relatedBricks: [
          // Include the lot itself as a related brick (clients lot → clients brick)
          body.currentLot,
          ...(body.screenContext?.relationships?.relatedBricks || []),
        ].filter(Boolean),
        // Screen context (when no modal)
        screenContext: body.screenContext?.contextMode !== 'modal_exclusive'
          ? body.screenContext
          : null,
        // Conversation history for multi-turn
        // Include current message since setMessages is async and messages is stale here
        conversationHistory: [...messages, userMessage].slice(-6).map(m => ({
          role: m.role,
          content: typeof m.content === 'string' ? m.content.substring(0, 500) : '',
        })),
      };

      // Get auth user ID directly from session (most reliable for RLS)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated. Please log in.');

      // INSERT into agent_commands — avengers.js picks this up via Realtime
      const { data: command, error: insertError } = await supabase
        .from('agent_commands')
        .insert({
          command_text: input,
          source: 'odv_truest',
          context,
          user_id: authUser.id,
          status: 'pending',
          expires_at: new Date(Date.now() + 5 * 60_000).toISOString(),
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      console.log('[AgentChat] Command inserted:', command.id.substring(0, 8));
      setPendingCommandId(command.id);

      // Subscribe to response via Realtime
      subscribeToResponse(command.id);

    } catch (err) {
      console.error('[AgentChat] Error:', err);
      setError(err);
      if (onError) onError(err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Failed to send command. Check your connection.',
        id: (Date.now() + 1).toString(),
        isError: true,
      }]);
      setIsLoading(false);
      setPendingPrompt(null);
    }
  }, [input, messages, body, isLoading, onError, subscribeToResponse]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) supabase.removeChannel(subscriptionRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setPendingCommandId(null);
    sessionStorage.removeItem('__ODV_PENDING_EVENT__');
  }, []);

  // sendMessage(text) — programmatic send for quick-reply chips, option selections, etc.
  // Same pipeline as handleSubmit but takes text directly instead of reading input state.
  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text, id: Date.now().toString() };
    setPendingPrompt(text);
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const context = {
        currentLot: body.currentLot,
        currentRoute: body.currentRoute,
        platform: 'web',
        selectedBrick: body.selectedBrick || null,
        modal: body.screenContext?.contextMode === 'modal_exclusive' ? {
          type: body.screenContext.modal?.type,
          entityType: body.screenContext.modal?.entityType,
          entityId: body.screenContext.modal?.entityId,
          entity: body.screenContext.modal?.entity,
          availableActions: body.screenContext.modal?.availableActions,
        } : null,
        brickHint: body.selectedBrick?.id
          || body.screenContext?.modal?.entity?.brick
          || body.screenContext?.relationships?.brick
          || null,
        relatedBricks: [
          body.currentLot,
          ...(body.screenContext?.relationships?.relatedBricks || []),
        ].filter(Boolean),
        screenContext: body.screenContext?.contextMode !== 'modal_exclusive'
          ? body.screenContext
          : null,
        conversationHistory: [...messages, userMessage].slice(-6).map(m => ({
          role: m.role,
          content: typeof m.content === 'string' ? m.content.substring(0, 500) : '',
        })),
      };

      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated.');

      const { data: command, error: insertError } = await supabase
        .from('agent_commands')
        .insert({
          command_text: text,
          source: 'odv_truest',
          context,
          user_id: authUser.id,
          status: 'pending',
          expires_at: new Date(Date.now() + 5 * 60_000).toISOString(),
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      setPendingCommandId(command.id);
      subscribeToResponse(command.id);
    } catch (err) {
      console.error('[AgentChat] Send error:', err);
      setError(err);
      if (onError) onError(err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Failed to send command. Check your connection.',
        id: (Date.now() + 1).toString(),
        isError: true,
      }]);
      setIsLoading(false);
      setPendingPrompt(null);
    }
  }, [messages, body, isLoading, onError, subscribeToResponse]);

  return { messages, input, setInput, handleInputChange, handleSubmit, sendMessage, isLoading, append, error, clearMessages, pendingCommandId, pendingPrompt };
};

const MotionButton = motion.button;
const MotionDiv = motion.div;

const looksLikeImageReference = (value) => {
  if (typeof value !== 'string') return false;
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('/assets/') ||
    value.endsWith('.svg') ||
    value.endsWith('.png') ||
    value.endsWith('.jpg') ||
    value.endsWith('.jpeg')
  );
};

const AgentAvatar = ({
  agent,
  size = 32,
  className = '',
  fallbackSize = 'text-xl'
}) => {
  const [hasError, setHasError] = useState(false);
  const avatarValue = agent?.avatar;
  const avatarEmoji =
    agent?.avatarEmoji ||
    agent?.emoji ||
    (avatarValue && !looksLikeImageReference(avatarValue) ? avatarValue : null);
  const candidateUrl = !hasError
    ? agent?.avatarUrl || (avatarValue && looksLikeImageReference(avatarValue) ? avatarValue : null)
    : null;

  return (
    <div
      className={`rounded-full flex items-center justify-center overflow-hidden bg-black ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      {candidateUrl ? (
        <img
          src={candidateUrl}
          alt={`${agent?.name || 'Agent'} avatar`}
          className="w-full h-full object-cover"
          onError={() => setHasError(true)}
        />
      ) : (
        <span className={fallbackSize}>{avatarEmoji || '🤖'}</span>
      )}
    </div>
  );
};

const STATUS_VISUALS = {
  checking: {
    label: 'Linking',
    textClass: 'text-amber-200',
    dotClass: 'bg-amber-400'
  },
  ready: {
    label: 'Online',
    textClass: 'text-emerald-200',
    dotClass: 'bg-emerald-400'
  },
  degraded: {
    label: 'Reconnecting',
    textClass: 'text-orange-200',
    dotClass: 'bg-orange-400'
  },
  offline: {
    label: 'Offline',
    textClass: 'text-red-200',
    dotClass: 'bg-red-400'
  }
};

const mapToStatusKey = (status) => {
  if (!status) return 'ready';
  const normalized = String(status).toLowerCase();

  if (['healthy', 'ready', 'ok', 'online'].includes(normalized)) return 'ready';
  if (['initializing', 'warming', 'booting', 'checking'].includes(normalized)) return 'checking';
  if (['degraded', 'retrying', 'pending'].includes(normalized)) return 'degraded';
  if (['offline', 'error', 'failed'].includes(normalized)) return 'offline';

  return STATUS_VISUALS[normalized] ? normalized : 'ready';
};

// Utility for relative time formatting (currently unused but may be needed for status display)
// eslint-disable-next-line no-unused-vars
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return null;
  const diff = Date.now() - new Date(timestamp).getTime();
  if (Number.isNaN(diff) || diff < 0) return null;
  if (diff < 15000) return 'just now';
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  return `${Math.round(diff / 3600000)}h ago`;
};

// Yoda Icon with purple pulsing animation when working
const YodaIcon = ({ isWorking = false, size = 'normal' }) => {
  const [logoError, setLogoError] = useState(false);
  const dimensions = size === 'small' ? 'w-8 h-8' : 'w-10 h-10';
  const imgSize = size === 'small' ? 'w-5 h-5' : 'w-7 h-7';

  return (
    <motion.div
      className={`relative ${dimensions}`}
      animate={isWorking ? {
        scale: [1, 1.08, 1],
      } : {}}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {/* Purple glow when working */}
      {isWorking && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(147,51,234,0.6) 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.8, 0.3, 0.8]
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      {/* Yoda container */}
      <div className={`absolute inset-0 rounded-full ${isWorking ? 'bg-purple-900/50 border-purple-500/50' : 'bg-black/80 border-white/15'} border flex items-center justify-center overflow-hidden`}>
        {!logoError ? (
          <img
            src="/images/yoda-icon.png"
            alt="Yoda"
            className={`${imgSize} object-contain`}
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="text-lg">🧘</span>
        )}
      </div>
      {/* Purple ring pulse when working */}
      {isWorking && (
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-purple-500"
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.6, 0, 0.6]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeOut"
          }}
        />
      )}
    </motion.div>
  );
};

// ODV Logo bubble icon (minimized state) - original styling
const AssistantIcon = () => {
  const [logoError, setLogoError] = useState(false);

  return (
    <motion.div
      className="relative w-14 h-14"
      animate={{
        scale: [1, 1.05, 1],
        rotate: [0, 2, -2, 0]
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F6E7A5] to-[#9E7B2F] opacity-90 animate-pulse" />
      <div className="absolute inset-[4px] rounded-full bg-black/80 border border-white/15 flex items-center justify-center overflow-hidden">
        {!logoError ? (
          <img
            src="/odv-logo.png"
            alt="ODV AI Assistant"
            className="w-8 h-8 object-contain"
            onError={() => setLogoError(true)}
          />
        ) : (
          <Sparkles size={22} className="text-[#F5E7AB]" />
        )}
      </div>
      {/* Animated ring effect */}
      <motion.div
        className="absolute inset-0 rounded-full border-2 border-[#F5E7AB]/30"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0, 0.5]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeOut"
        }}
      />
    </motion.div>
  );
};

const STORAGE_KEYS = {
  minimized: 'odv-ai-chat-minimized',
  messages: 'odv-ai-chat-messages',
  panelMode: 'odv-ai-chat-panel-mode'
};

// Persist messages to localStorage
const saveMessages = (messages) => {
  try {
    // Only save last 50 messages to avoid storage bloat
    const toSave = messages.slice(-50);
    localStorage.setItem(STORAGE_KEYS.messages, JSON.stringify(toSave));
  } catch (e) {
    console.warn('[Chat] Failed to save messages:', e);
  }
};

const loadMessages = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.messages);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Filter out any malformed messages
      return parsed.filter(m => m.role && m.content);
    }
  } catch (e) {
    console.warn('[Chat] Failed to load messages:', e);
  }
  return [];
};

const AgentChatBubble = ({ authMode = false, authContext = null, onClose = null }) => {
  const [isMinimized, setIsMinimized] = useState(() => {
    // Auth mode always starts expanded
    if (authMode) return false;
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(STORAGE_KEYS.minimized);
    return stored ? stored === 'true' : true;
  });
  // Panel mode: true = slide-out panel, false = floating bubble chat
  const [isPanelMode, setIsPanelMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(STORAGE_KEYS.panelMode);
    return stored !== 'false'; // Default to panel mode
  });
  // Load persisted messages on mount
  const [initialMessages] = useState(() => loadMessages());
  const [activeAgent, setActiveAgent] = useState(null);
  const [agentSuggestions, setAgentSuggestions] = useState([]);
  const [showSuggestionPanel, setShowSuggestionPanel] = useState(false);
  const [suggestionPrefill] = useState({ title: '', description: '', type: 'new_feature' });
  const [aiConnection, setAiConnection] = useState({
    state: 'checking',
    latency: null,
    updatedAt: null,
    orchestrator: null,
    error: null
  });
  // Locked configuration mode state
  const [lockedMode, setLockedMode] = useState(null); // { clientId, clientName, sessionId, configState }
  // Brick toolbox - selected specialty for instant expertise
  const [selectedBrick, setSelectedBrick] = useState(null);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const { user, userData, userRole } = useAuth();

  // Modal focus awareness - when a modal is open, it becomes prime context
  const { focusedModal, getContextSummary } = useModalFocus();
  // Screen context from lot components (selected client, visible data, etc.)
  const { screenData: liveScreenData } = useScreenContext();

  const currentLot = useMemo(() => {
    const path = location.pathname;
    if (path.includes('assignments')) return 'assignments';
    if (path.includes('equipment')) return 'equipment';
    if (path.includes('onboarding')) return 'onboarding';
    if (path.includes('agency')) return 'agency';
    if (path.includes('finance')) return 'finance';
    if (path.includes('clients')) return 'clients';
    if (path.includes('valets')) return 'valets';
    if (path.includes('events')) return 'events';
    if (path.includes('invoices')) return 'invoices';
    if (path.includes('badges')) return 'badges';
    if (path.includes('recruiting')) return 'recruiting';
    if (path.includes('training')) return 'training';
    if (path.includes('gamification')) return 'gamification';
    return 'dashboard';
  }, [location.pathname]);

  // Build rich screen context for AI awareness
  // IMPORTANT: When a modal is open, it BLOCKS all other context - modal is exclusive focus
  const screenContext = useMemo(() => {
    // ═══════════════════════════════════════════════════════════════════════════
    // MODAL FOCUS = EXCLUSIVE CONTEXT
    // When a modal is open, ONLY that modal's context matters
    // This blocks page context, URL context, everything else
    // ═══════════════════════════════════════════════════════════════════════════
    if (focusedModal) {
      const summary = getContextSummary();
      return {
        // Exclusive modal context - nothing else
        contextMode: 'modal_exclusive',
        focusType: 'modal',
        modal: {
          type: focusedModal.modalType,
          entityType: focusedModal.entityType,
          entityId: focusedModal.entityId,
          entity: focusedModal.entityData,
          availableActions: focusedModal.availableActions,
          relationships: focusedModal.relationships
        },
        // AI instructions
        instruction: `User is viewing ${focusedModal.entityType} in ${focusedModal.modalType} modal. ONLY respond about this ${focusedModal.entityType}. Ignore page context.`,
        aiHint: summary?.hint,
        canDo: summary?.canDo
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // NO MODAL = Normal page context
    // ═══════════════════════════════════════════════════════════════════════════
    const context = {
      contextMode: 'page',
      view: currentLot,
      route: location.pathname,
      params: {},
      selectedDate: null,
      selectedClient: null,
      selectedLocation: null,
      visibleData: null
    };

    // Parse URL for entity IDs (e.g., /assignments/abc123, /clients/xyz)
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2) {
      const entityType = pathParts[0];
      const entityId = pathParts[1];
      if (entityId && !['new', 'edit', 'create'].includes(entityId)) {
        context.params[entityType + 'Id'] = entityId;
      }
    }

    // Check for query params (e.g., ?date=2025-12-04&client=tcnj&week=51&year=2025)
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('date')) context.selectedDate = searchParams.get('date');
    if (searchParams.get('client')) context.selectedClient = searchParams.get('client');
    if (searchParams.get('location')) context.selectedLocation = searchParams.get('location');
    if (searchParams.get('week')) context.selectedWeek = parseInt(searchParams.get('week'));
    if (searchParams.get('year')) context.selectedYear = parseInt(searchParams.get('year'));
    if (searchParams.get('month')) context.selectedMonth = parseInt(searchParams.get('month'));
    if (searchParams.get('mode')) context.viewMode = searchParams.get('mode'); // 'week' or 'month'
    if (searchParams.get('garage')) context.selectedGarage = searchParams.get('garage');
    if (searchParams.get('location_id')) context.selectedLocationId = searchParams.get('location_id');

    // Merge live screen context from ScreenContextProvider (reactive — updates when lot components call setScreenContext)
    if (liveScreenData) {
      Object.assign(context, liveScreenData);
    }

    // Include locked mode data for client configuration sessions
    if (lockedMode) {
      context.lockedMode = true;
      context.configSession = {
        clientId: lockedMode.clientId,
        clientName: lockedMode.clientName,
        sessionId: lockedMode.sessionId,
        configState: lockedMode.configState
      };
    }

    return context;
  }, [currentLot, location.pathname, location.search, lockedMode, focusedModal, getContextSummary, liveScreenData]);

  // Screen context is available for our harness via screenContext variable
  // (CopilotKit removed - using native ODV harness instead)

  const { messages, input, setInput, handleInputChange, handleSubmit, sendMessage, isLoading, append, error: chatError, clearMessages, pendingPrompt, pendingCommandId } = useAgentCommands({
    body: {
      userId: user?.id,
      userName: userData?.name || user?.email,
      userRole,
      currentLot,
      currentRoute: location.pathname,
      platform: 'web',
      screenContext,
      authMode,
      authContext,
      lockedMode: lockedMode ? {
        clientId: lockedMode.clientId,
        clientName: lockedMode.clientName,
        sessionId: lockedMode.sessionId,
        configState: lockedMode.configState
      } : null,
      selectedBrick: selectedBrick ? {
        id: selectedBrick.id,
        name: selectedBrick.name,
        personality: selectedBrick.personality
      } : null,
      // Modal relationships — tells PromptAssembler which bricks to load
      relationships: focusedModal?.relationships || null,
    },
    onFinish: async (message) => {
      console.log('[Chat] Message finished:', {
        role: message.role,
        content: message.content?.substring(0, 100),
        hasActions: !!(message.actions && message.actions.length > 0),
        needsMoreInfo: message.needsMoreInfo
      });

      // Check if AI needs more info (multi-turn conversation)
      if (message.needsMoreInfo && message.partialData) {
        // Store partial data for next message context
        sessionStorage.setItem('__ODV_PENDING_EVENT__', JSON.stringify({
          partialData: message.partialData,
          timestamp: Date.now()
        }));
        console.log('[Chat] Stored partial event data for follow-up');
        // The message already contains the follow-up questions, no action needed
        return;
      }

      // Execute actions if present (same as voice PTT)
      if (message.actions && message.actions.length > 0) {
        try {
          const { default: ActionExecutor } = await import('@/services/ActionExecutor');
          const result = await ActionExecutor.executeActions(message.actions, {
            userId: user?.id,
            userRole,
            currentLot,
            currentRoute: location.pathname,
            screenContext,
            onRefresh: () => {
              // Trigger data refresh if needed
              if (window.location) {
                window.location.reload();
              }
            }
          });

          if (result.executed && result.executed.length > 0) {
            console.log('[Chat] Actions executed:', result.executed);

            // Check if any action needs more input (e.g., schedule_event with missing info)
            for (const execResult of result.executed) {
              if (execResult.result?.needsInput) {
                // Append follow-up message from AI
                append({
                  role: 'assistant',
                  content: execResult.result.message || 'I need more information to complete this action.',
                  needsMoreInfo: true,
                  missingInfo: execResult.result.missingInfo,
                  partialData: execResult.result.partialData
                });
                break;
              }
            }
          }

          // Clear pending event data on successful action
          sessionStorage.removeItem('__ODV_PENDING_EVENT__');
        } catch (actionError) {
          console.error('[Chat] Error executing actions:', actionError);
        }
      }

      // Handle navigation if present
      if (message.navigation) {
        if (message.navigation.route) {
          // Try React Router first for smoother navigation
          if (window.__ODV_NAVIGATE__) {
            window.__ODV_NAVIGATE__(message.navigation.route);
          } else {
            // Fallback to full page navigation
            window.location.href = message.navigation.route;
          }
        }
      }

      // Handle UI actions (select_entity, open_modal, show_confirmation)
      if (message.uiAction) {
        const { type, entityType, entityId, entityData, modalType } = message.uiAction;

        if (type === 'select_entity') {
          // Dispatch event for entity selection
          window.dispatchEvent(new CustomEvent('odv-select-entity', {
            detail: { entityType, entityId, entityData, source: 'ai-chat' }
          }));
        }

        if (type === 'open_modal') {
          // Dispatch event to open modal
          window.dispatchEvent(new CustomEvent('odv-open-modal', {
            detail: { modalType, entityId, entityType, source: 'ai-chat' }
          }));
        }

        if (type === 'show_confirmation') {
          // Keep confirmation in chat for user to approve/reject
          console.log('[Chat] Showing confirmation:', message.preview);
        }
      }
    },
    onError: (error) => {
      console.error('[Chat] Error from useChat hook:', {
        error,
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
    },
    // Load persisted conversation history
    initialMessages
  });
  
  // Debug: Log when messages change
  useEffect(() => {
    console.log('[Chat] Messages updated:', {
      count: messages.length,
      lastMessage: messages[messages.length - 1] ? {
        role: messages[messages.length - 1].role,
        contentLength: messages[messages.length - 1].content?.length,
        content: messages[messages.length - 1].content?.substring(0, 100)
      } : null,
      isLoading,
      error: chatError?.message
    });
  }, [messages, isLoading, chatError]);

  useEffect(() => {
    // DISABLED: Polling every 15s causes console spam and unnecessary load
    // Only poll when chat is open and user is actively engaging
    if (isMinimized) return;

    const checkSuggestions = async () => {
      try {
        const response = await fetch('/api/chat/suggestions', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': user?.id
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.suggestions)) {
            setAgentSuggestions(data.suggestions);
          }
        }
      } catch {
        // Optional feature
      }
    };

    // Only poll when chat is open, and reduce frequency to 60s (was 15s)
    const interval = setInterval(checkSuggestions, 60000);
    checkSuggestions(); // Initial fetch

    return () => clearInterval(interval);
  }, [user?.id, isMinimized]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.minimized, String(isMinimized));
  }, [isMinimized]);

  // Persist panel mode preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.panelMode, String(isPanelMode));
  }, [isPanelMode]);

  // Persist messages when they change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessages(messages);
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && !isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  const refreshAiConnection = useCallback(async () => {
    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      // Status check: verify Supabase auth session is active (pipeline uses agent_commands table)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const latency = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime);

      setAiConnection({
        state: authUser ? 'ready' : 'offline',
        latency,
        updatedAt: new Date().toISOString(),
        orchestrator: 'avengers',
        error: authUser ? null : 'Not authenticated'
      });
    } catch (error) {
      console.error('[AI Status] Error:', error.message || error);
      setAiConnection({
        state: 'offline',
        latency: null,
        updatedAt: new Date().toISOString(),
        orchestrator: null,
        error: error.message
      });
    }
  }, []);

  useEffect(() => {
    // Only check status when chat is open (not minimized)
    if (isMinimized) return;

    refreshAiConnection(); // Initial check
    const interval = setInterval(refreshAiConnection, 60000); // Check every 60s when open
    return () => {
      clearInterval(interval);
    };
  }, [refreshAiConnection, isMinimized]);

  // Listen for modal focus changes - context awareness only (no interruption)
  // The chat bubble stays minimized but knows what modal is open when user opens it
  useEffect(() => {
    const handleModalFocusChange = (event) => {
      const { action, modal } = event.detail || {};

      if (action === 'focus' && modal) {
        console.log('[AgentChatBubble] Modal context set:', modal.modalType, modal.entityId);
        // Context is already set via focusedModal from useModalFocus()
        // No auto-expand, no proactive message - just silent awareness
      }

      if (action === 'blur' && modal) {
        console.log('[AgentChatBubble] Modal context cleared:', modal?.modalType);
      }
    };

    window.addEventListener('modal-focus-change', handleModalFocusChange);
    return () => window.removeEventListener('modal-focus-change', handleModalFocusChange);
  }, []);

  // Listen for AI context updates from other components (locked config mode, events, etc.)
  useEffect(() => {
    const handleAIContextUpdate = (event) => {
      const { action, eventId, eventName, clientName, eventDate, lockedMode: isLocked, sessionId, clientId, configState } = event.detail || {};

      console.log('[AgentChatBubble] AI context update received:', { action, clientName, isLocked });

      // Handle locked configuration mode
      if (action === 'client_config_locked') {
        setLockedMode({ clientId, clientName, sessionId, configState });

        // Auto-expand chat when entering locked mode
        if (isMinimized) {
          setIsMinimized(false);
        }

        // Clear previous messages and add locked mode intro
        clearMessages();
        setTimeout(() => {
          const missingItems = configState?.missing?.length > 0 ? configState.missing.join(', ') : null;
          const warnings = configState?.warnings?.length > 0 ? configState.warnings.join('; ') : null;

          append({
            role: 'assistant',
            content: `**LOCKED CONFIGURATION MODE: ${clientName}**\n\nI'm now focused exclusively on configuring this client. I'll maintain context throughout our session.\n\n${missingItems ? `**Missing:** ${missingItems}\n` : ''}${warnings ? `**Warnings:** ${warnings}\n` : ''}\nTell me what you'd like to configure. I understand:\n• Billing model & revenue splits\n• Service agreement rates (valet, bellhop, shuttle)\n• Invoicing & minimum guarantees\n\nWhat should we set up?`,
            isProactive: true,
            lockedConfig: true
          });
        }, 100);
      }

      // Handle unlocked (config session ended)
      if (action === 'client_config_unlocked') {
        const changes = event.detail?.changesApplied || [];
        if (lockedMode) {
          append({
            role: 'assistant',
            content: changes.length > 0
              ? `**Configuration session ended for ${lockedMode.clientName}**\n\nChanges applied: ${changes.join(', ')}`
              : `**Configuration session ended for ${lockedMode.clientName}**`,
            isProactive: true
          });
        }
        setLockedMode(null);
      }

      // Handle event created (existing functionality)
      if (action === 'event_created') {
        if (isMinimized) {
          setIsMinimized(false);
        }
        append({
          role: 'assistant',
          content: `**Event Created: ${eventName}**\n\nI noticed you just created an event${clientName ? ` for ${clientName}` : ''}${eventDate ? ` on ${eventDate}` : ''}.\n\nWould you like me to help with:\n• **Draft service agreement** - Create contract for client signature\n• **Create assignments** - Set up valet positions for this event\n• **Set up products** - Configure billing items\n\nJust say "draft agreement" or "create assignments" to get started!`,
          isProactive: true,
          eventContext: { eventId, eventName, clientName, eventDate }
        });
      }
    };

    window.addEventListener('ai-context-update', handleAIContextUpdate);
    return () => window.removeEventListener('ai-context-update', handleAIContextUpdate);
  }, [append, isMinimized, lockedMode, clearMessages]);

  // Listen for 'open-ai-chat' events from other components (e.g., ClientConfigModal Config tab)
  useEffect(() => {
    const handleOpenAIChat = (event) => {
      const { prefill, context } = event.detail || {};

      console.log('[AgentChatBubble] Open AI chat requested:', { prefill, context });

      // Expand chat if minimized
      if (isMinimized) {
        setIsMinimized(false);
      }

      // Prefill input if provided
      if (prefill && setInput) {
        setInput(prefill);
      }

      // If context is client_configuration, add a helpful prompt
      if (context === 'client_configuration') {
        setTimeout(() => {
          append({
            role: 'assistant',
            content: `I'm ready to help configure this client. I can see the full configuration above.\n\nTry asking me to:\n• "Set up a 50/50 revenue split"\n• "Add shuttle billing at $35/hour"\n• "Configure minimum guarantee of $500/week"\n• "Set up bellhop service at flat weekly rate"\n\nWhat would you like to configure?`,
            isProactive: true,
            configContext: true
          });
        }, 100);
      }
    };

    window.addEventListener('open-ai-chat', handleOpenAIChat);
    return () => window.removeEventListener('open-ai-chat', handleOpenAIChat);
  }, [isMinimized, setInput, append]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // eslint-disable-next-line no-unused-vars
  const handleSuggestionClick = (suggestion) => {
    if (!suggestion) return;
    append({
      role: 'user',
      content: suggestion.message || suggestion.text
    });
    setAgentSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
  };

  // eslint-disable-next-line no-unused-vars
  const primarySuggestion = agentSuggestions[0];

  return (
    <AnimatePresence initial={false} mode="wait">
      {isMinimized && !authMode ? (
        <MotionButton
          key="chat-toggle"
          type="button"
          aria-label="Open AI Assistant"
          onClick={() => setIsMinimized(false)}
          initial={{ opacity: 0, scale: 0.85, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 40 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-2 group"
        >
          <motion.div
            animate={{
              boxShadow: [
                '0 0 0 0 rgba(245, 231, 171, 0.4)',
                '0 0 0 10px rgba(245, 231, 171, 0)',
                '0 0 0 0 rgba(245, 231, 171, 0)'
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeOut"
            }}
            className="rounded-full"
          >
            <AssistantIcon />
          </motion.div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-white/60 group-hover:text-[#F5E7AB] transition-colors">AI</div>
          {agentSuggestions.length > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-[10px] text-white/70 bg-[#F5E7AB]/20 border border-[#F5E7AB]/30 rounded-full px-2 py-0.5"
            >
              +{agentSuggestions.length}
            </motion.span>
          )}
        </MotionButton>
      ) : isPanelMode ? (
        /* ═══════════════════════════════════════════════════════════════════════════
         * SLIDE-OUT PANEL MODE - Full height, wider, better for real conversations
         * ═══════════════════════════════════════════════════════════════════════════ */
        <MotionDiv
          key="chat-slide-panel"
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed top-0 right-0 bottom-0 z-50 w-[450px] flex flex-col"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.98) 0%, rgba(10,10,15,0.98) 100%)',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '-20px 0 60px rgba(0,0,0,0.5)'
          }}
        >
          {/* Panel Header */}
          <header className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${lockedMode ? 'border-[#AEA36D]/30 bg-[#AEA36D]/5' : 'border-white/10'}`}>
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Yoda icon */}
              <motion.div
                className="relative w-10 h-10"
                animate={isLoading ? { scale: [1, 1.05, 1] } : {}}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                {isLoading && (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(147,51,234,0.4) 0%, transparent 70%)' }}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0.2, 0.6] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                  />
                )}
                <img
                  src="/images/yoda-icon.png"
                  alt="Yoda"
                  className={`w-10 h-10 object-contain transition-opacity duration-300 ${isLoading ? 'opacity-90' : 'opacity-70'}`}
                />
              </motion.div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-lg text-[#AEA36D] font-mono font-bold tracking-wide">ODV AI</p>
                  <StatusBadge connection={aiConnection} />
                </div>
                {screenContext?.view && screenContext.view !== 'dashboard' && (
                  <p className="text-xs text-white/40 font-mono truncate mt-0.5">
                    {screenContext.selectedClient?.name || screenContext.selectedClient?.display_name || screenContext.view}
                    {screenContext.weekNumber && ` • Week ${screenContext.weekNumber}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Clear conversation */}
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    clearMessages();
                    localStorage.removeItem(STORAGE_KEYS.messages);
                  }}
                  className="w-8 h-8 rounded-lg border border-white/10 text-white/40 hover:text-red-400 hover:border-red-400/30 flex items-center justify-center transition-colors"
                  title="Clear conversation"
                >
                  <Trash2 size={14} />
                </button>
              )}
              {/* Toggle to bubble mode */}
              <button
                type="button"
                onClick={() => setIsPanelMode(false)}
                className="w-8 h-8 rounded-lg border border-white/10 text-white/40 hover:text-white hover:border-white/30 flex items-center justify-center transition-colors"
                title="Switch to bubble mode"
              >
                <Minus size={14} />
              </button>
              {/* Close/minimize */}
              <button
                type="button"
                onClick={() => {
                  if (authMode && onClose) {
                    onClose();
                  } else {
                    setIsMinimized(true);
                  }
                }}
                className="w-8 h-8 rounded-lg border border-white/10 text-white/40 hover:text-white hover:border-white/30 flex items-center justify-center transition-colors"
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          </header>

          {/* Brick Toolbox - Select specialty for instant expertise */}
          <BrickToolbox
            selectedBrick={selectedBrick?.id}
            onBrickSelect={(brick) => setSelectedBrick(brick)}
            screenContext={screenContext}
          />

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <img
                  src="/odv-logo-car.png"
                  alt=""
                  className="w-24 h-24 object-contain opacity-20 mx-auto mb-4"
                />
                <p className="text-[#AEA36D] font-mono text-lg mb-2">
                  {selectedBrick ? `${selectedBrick.name} Mode` : 'Ready to help'}
                </p>
                <p className="text-white/40 text-sm">
                  {selectedBrick
                    ? selectedBrick.description
                    : screenContext?.view === 'assignments' && screenContext?.visibleAssignments?.length > 0
                    ? `Viewing ${screenContext.assignmentCount} assignments for ${screenContext.selectedClient?.name || 'this client'}`
                    : screenContext?.view === 'payroll'
                    ? `Payroll for week ${screenContext.weekNumber || '?'}`
                    : 'Select a specialty above or ask anything'
                  }
                </p>
              </motion.div>
            )}

            {/* Messages */}
            <AnimatePresence>
              {messages.map((msg, index) => {
                const isUser = msg.role === 'user';
                const agentInfo = msg.agent || activeAgent;
                const isProgress = msg.isProgress;
                const isComplete = msg.isComplete;
                const isError = msg.isError;

                if (isProgress) {
                  return (
                    <motion.div
                      key={msg.id || index}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="font-mono text-sm py-1 text-purple-400"
                    >
                      <span className="mr-2">→</span>
                      {msg.content}
                    </motion.div>
                  );
                }

                if (isComplete) {
                  return (
                    <motion.div
                      key={msg.id || index}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="font-mono text-sm py-2 px-3 bg-emerald-500/10 border-l-2 border-emerald-500 rounded-r"
                    >
                      <span className="text-emerald-400 mr-2">✓</span>
                      {msg.content}
                    </motion.div>
                  );
                }

                // Q&A messages — questions with options or thinking transitions (fullscreen)
                if (!isUser && (msg.options || msg.isThinking)) {
                  const isAnswered = messages.slice(index + 1).some(m => m.role === 'user');
                  return (
                    <QAMessage
                      key={msg.id || index}
                      msg={msg}
                      onOptionSelect={(option) => sendMessage(option)}
                      answered={isAnswered}
                      agentInfo={agentInfo}
                      isError={isError}
                      mode="fullscreen"
                    />
                  );
                }

                return (
                  <motion.div
                    key={msg.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`${isUser ? 'flex justify-end' : ''}`}
                  >
                    {isUser ? (
                      <div className="max-w-[85%] bg-[#AEA36D]/20 border border-[#AEA36D]/30 rounded-2xl rounded-br-sm px-4 py-3">
                        <p className="text-[#AEA36D] text-sm">{msg.content}</p>
                      </div>
                    ) : (
                      <div className={`max-w-[90%] ${isError ? 'bg-red-500/10 border-red-500/30' : 'bg-white/5 border-white/10'} border rounded-2xl rounded-bl-sm px-4 py-3`}>
                        {agentInfo && (
                          <div className="text-[10px] text-purple-400 font-mono mb-1">
                            [{agentInfo.name || 'logic'}]
                          </div>
                        )}
                        <p className={`text-sm whitespace-pre-wrap ${isError ? 'text-red-400' : 'text-white/90'}`}>
                          {msg.content}
                        </p>
                        {/* Applied changes indicator */}
                        {msg.appliedChanges && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <div className="text-[10px] text-emerald-400 font-mono">
                              ✓ {msg.appliedChanges.length} change(s) applied
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Agent thinking animation */}
            {isLoading && pendingPrompt && (
              <AgentThinkingAnimation
                prompt={pendingPrompt}
                commandId={pendingCommandId}
                mode="fullscreen"
                brickHint={selectedBrick?.id || currentLot}
              />
            )}
            {/* Fallback: loading without prompt (shouldn't happen) */}
            {isLoading && !pendingPrompt && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 text-purple-400 font-mono text-sm"
              >
                <Loader2 size={14} className="animate-spin" />
                <span>thinking...</span>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!input?.trim() || isLoading) return;
              handleSubmit(e);
            }}
            className="p-4 border-t border-white/10 bg-black/50"
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-3 rounded-xl border border-white/10 bg-black/50 px-4 py-3">
                <span className="text-[#AEA36D] font-mono font-bold select-none">❯</span>
                <input
                  value={input}
                  onChange={handleInputChange}
                  placeholder="Ask anything..."
                  className="flex-1 bg-transparent text-white placeholder-white/30 focus:outline-none"
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="rounded-xl bg-gradient-to-br from-[#F6E7AB] to-[#9E7B2F] px-5 py-3 text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </form>

          {/* Feature Suggestion Panel */}
          <FeatureSuggestionPanel
            isOpen={showSuggestionPanel}
            onClose={() => setShowSuggestionPanel(false)}
            user={user}
            prefillTitle={suggestionPrefill.title}
            prefillDescription={suggestionPrefill.description}
            prefillType={suggestionPrefill.type}
          />
        </MotionDiv>
      ) : (
        /* ═══════════════════════════════════════════════════════════════════════════
         * BUBBLE MODE - Original floating chat bubble
         * ═══════════════════════════════════════════════════════════════════════════ */
        <MotionDiv
          key="chat-panel"
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 30 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-6 right-6 z-50 w-[380px]"
        >
          <motion.div
            className="flex flex-col h-[500px] rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.95) 100%)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.1)'
            }}
          >
            <header className={`flex items-center justify-between gap-2 p-3 border-b ${lockedMode ? 'border-[#AEA36D]/30 bg-[#AEA36D]/5' : 'border-white/10'}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Terminal-style status indicator */}
                <div className={`w-8 h-8 rounded-lg ${isLoading ? 'bg-purple-900/50 border-purple-500/50' : 'bg-black/50 border-white/10'} border flex items-center justify-center`}>
                  <motion.span
                    className={`text-lg ${isLoading ? 'text-purple-400' : 'text-[#AEA36D]'}`}
                    animate={isLoading ? { opacity: [1, 0.5, 1] } : {}}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    {isLoading ? '◉' : '▸'}
                  </motion.span>
                </div>
                <div className="min-w-0 flex-1">
                  {lockedMode ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#AEA36D] animate-pulse" />
                        <p className="text-sm text-[#AEA36D] font-mono font-semibold truncate">Config Mode</p>
                      </div>
                      <p className="text-[10px] text-white/50 font-mono truncate">{lockedMode.clientName}</p>
                    </>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm text-[#AEA36D] font-mono font-bold tracking-wide">LOGIC</p>
                        <StatusBadge connection={aiConnection} />
                      </div>
                      {/* YODA KNOWS indicator */}
                      <div className="flex items-center gap-1.5">
                        <motion.span
                          className={`text-[10px] font-mono tracking-widest ${isLoading ? 'text-purple-400' : 'text-white/40'}`}
                          animate={isLoading ? { opacity: [0.5, 1, 0.5] } : {}}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          YODA KNOWS
                        </motion.span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {/* Clear conversation button */}
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={clearMessages}
                    className="w-7 h-7 rounded-full border border-white/15 text-white/50 hover:text-red-400 hover:border-red-400/50 flex items-center justify-center transition-colors"
                    title="Clear conversation"
                  >
                    <RotateCcw size={12} />
                  </button>
                )}
                {/* Expand to panel mode */}
                <button
                  type="button"
                  onClick={() => setIsPanelMode(true)}
                  className="w-7 h-7 rounded-full border border-white/15 text-white/50 hover:text-[#AEA36D] hover:border-[#AEA36D]/50 flex items-center justify-center transition-colors"
                  title="Expand to panel"
                >
                  <Maximize2 size={12} />
                </button>
                {/* Minimize/Close button - centered icon */}
                <button
                  type="button"
                  onClick={() => {
                    if (authMode && onClose) {
                      onClose();
                    } else {
                      setIsMinimized(true);
                    }
                  }}
                  className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
                    lockedMode
                      ? 'border-[#AEA36D]/30 text-[#AEA36D]/70 hover:text-[#AEA36D] hover:border-[#AEA36D]/50'
                      : 'border-white/15 text-white/70 hover:text-white'
                  }`}
                  title={authMode ? "Close" : lockedMode ? "Exit config mode" : "Minimize"}
                >
                  {authMode ? <X size={14} /> : <Minus size={14} />}
                </button>
              </div>
            </header>


            <div className="flex-1 overflow-y-auto p-4 space-y-3 relative">
              {/* Background: ODV Logo + Yoda Icon in center */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-3">
                {/* Yoda Icon with purple glow when loading */}
                <motion.div
                  className="relative"
                  animate={isLoading ? { scale: [1, 1.05, 1] } : {}}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  {isLoading && (
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ background: 'radial-gradient(circle, rgba(147,51,234,0.4) 0%, transparent 70%)' }}
                      animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0.2, 0.6] }}
                      transition={{ duration: 1.2, repeat: Infinity }}
                    />
                  )}
                  <img
                    src="/images/yoda-icon.png"
                    alt="Yoda"
                    className={`w-20 h-20 object-contain transition-opacity duration-300 ${isLoading ? 'opacity-70' : 'opacity-40'}`}
                  />
                  {isLoading && (
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-purple-500/50"
                      animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  )}
                </motion.div>
                {/* ODV Logo below Yoda */}
                <img
                  src="/odv-logo-car.png"
                  alt=""
                  className="w-24 h-24 object-contain opacity-30"
                />
              </div>
              
              {messages.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-mono text-sm relative z-10 pt-4"
                >
                  {lockedMode ? (
                    <div className="space-y-2">
                      <div className="text-[#AEA36D]">
                        <span className="text-purple-400">[config]</span> locked to: <span className="text-white">{lockedMode.clientName}</span>
                      </div>
                      {lockedMode.configState?.missing?.length > 0 && (
                        <div className="text-amber-400/80 text-xs pl-4 border-l border-amber-500/30">
                          missing: {lockedMode.configState.missing.join(', ')}
                        </div>
                      )}
                      <div className="text-white/40 text-xs mt-3">
                        type to configure billing, rates, or services...
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-[#AEA36D]">
                        <span className="text-purple-400">[ready]</span> {authMode ? 'auth mode' : 'awaiting input'}
                      </div>
                      <div className="text-white/40 text-xs">
                        {authMode
                          ? "login issues, password resets, account problems"
                          : "ask anything — context-aware"}
                      </div>
                      {screenContext?.view && screenContext.view !== 'dashboard' && (
                        <div className="text-[10px] text-white/30 mt-2">
                          <span className="text-purple-400/70">ctx:</span> {screenContext.view}
                          {screenContext.selectedWeek && ` • week ${screenContext.selectedWeek}`}
                          {screenContext.currentLot?.name && ` • ${screenContext.currentLot.name}`}
                        </div>
                      )}
                      {authMode && authContext?.email && (
                        <div className="text-[#AEA36D] text-xs mt-2">
                          user: {authContext.email}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              {/* Terminal-style message feed */}
              <AnimatePresence>
                {messages.map((msg, index) => {
                  const isUser = msg.role === 'user';
                  const agentInfo = msg.agent || activeAgent;
                  const isProgress = msg.isProgress;
                  const isComplete = msg.isComplete;
                  const isError = msg.isError;

                  // Progress messages - terminal style
                  if (isProgress) {
                    return (
                      <motion.div
                        key={msg.id || index}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="font-mono text-xs py-0.5"
                      >
                        <span className="text-purple-400">→ </span>
                        <span className="text-[#AEA36D]/80">{msg.content}</span>
                        {msg.progressStep && msg.progressTotal && (
                          <span className="text-white/30 ml-2">
                            [{msg.progressStep}/{msg.progressTotal}]
                          </span>
                        )}
                      </motion.div>
                    );
                  }

                  // Complete messages - terminal success style
                  if (isComplete) {
                    return (
                      <motion.div
                        key={msg.id || index}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="font-mono text-xs py-1 border-l-2 border-emerald-500/50 pl-2"
                      >
                        <span className="text-emerald-400">✓ </span>
                        <span className="text-emerald-300/90">{msg.content}</span>
                        {msg.summary?.links && (
                          <div className="flex gap-3 mt-1 pl-3">
                            {msg.summary.links.event && (
                              <a href={msg.summary.links.event} className="text-[#aea36d] hover:underline text-[10px]">
                                [view event]
                              </a>
                            )}
                            {msg.summary.links.assignments && (
                              <a href={msg.summary.links.assignments} className="text-[#aea36d] hover:underline text-[10px]">
                                [view assignments]
                              </a>
                            )}
                            {msg.summary.links.agreement && (
                              <a href={msg.summary.links.agreement} className="text-[#aea36d] hover:underline text-[10px]">
                                [view agreement]
                              </a>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  }

                  // Q&A messages — questions with options or thinking transitions
                  if (!isUser && (msg.options || msg.isThinking)) {
                    const isAnswered = messages.slice(index + 1).some(m => m.role === 'user');
                    return (
                      <QAMessage
                        key={msg.id || index}
                        msg={msg}
                        onOptionSelect={(option) => sendMessage(option)}
                        answered={isAnswered}
                        agentInfo={agentInfo}
                        isError={isError}
                        mode="bubble"
                      />
                    );
                  }

                  // Terminal-style messages
                  return (
                    <motion.div
                      key={msg.id || index}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15, delay: index * 0.02 }}
                      className="font-mono text-sm leading-relaxed py-1"
                    >
                      {isUser ? (
                        // User input - prompt style
                        <div className="flex items-start gap-2">
                          <span className="text-[#AEA36D] font-bold select-none">❯</span>
                          <span className="text-[#AEA36D]">{msg.content}</span>
                        </div>
                      ) : (
                        // AI response - terminal output style (Vegas gold monospace)
                        <div className={`pl-4 border-l-2 ${isError ? 'border-red-500/50' : 'border-[#AEA36D]/30'}`}>
                          {agentInfo && (
                            <div className="flex items-center gap-1.5 mb-1 text-[10px] text-white/40">
                              <span className="text-purple-400">[{agentInfo.name || 'logic'}]</span>
                            </div>
                          )}
                          <p className={`whitespace-pre-wrap text-sm leading-relaxed ${isError ? 'text-red-400' : 'text-[#AEA36D]'}`}>
                            {msg.content}
                          </p>
                          {/* Applied changes indicator */}
                          {msg.appliedChanges && msg.appliedChanges.length > 0 && (
                            <div className="mt-2 text-[10px] text-emerald-400/70">
                              <span className="text-emerald-500">applied:</span>{' '}
                              {msg.appliedChanges.map((change, i) => (
                                <span key={i} className="text-emerald-400">
                                  {i > 0 && ', '}{change}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Agent thinking animation */}
              {isLoading && pendingPrompt && (
                <AgentThinkingAnimation
                  prompt={pendingPrompt}
                  commandId={pendingCommandId}
                  mode="bubble"
                  brickHint={selectedBrick?.id || currentLot}
                />
              )}
              {/* Fallback */}
              {isLoading && !pendingPrompt && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-mono text-sm py-1 pl-4 border-l-2 border-purple-500/50"
                >
                  <div className="flex items-center gap-1 text-[#AEA36D]/70">
                    <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 0.8, repeat: Infinity }}>▌</motion.span>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <form 
              onSubmit={(e) => {
                console.log('[Chat] 🔵 Form submit triggered:', {
                  input: input?.substring(0, 50),
                  inputLength: input?.length,
                  isEmpty: !input?.trim(),
                  isLoading,
                  messagesCount: messages.length,
                  hasUser: !!user?.id
                });
                
                if (!input?.trim()) {
                  console.warn('[Chat] ⚠️ Cannot submit empty message');
                  e.preventDefault();
                  return;
                }
                
                if (isLoading) {
                  console.warn('[Chat] ⚠️ Already loading, skipping submit');
                  e.preventDefault();
                  return;
                }
                
                console.log('[Chat] 🔵 Calling handleSubmit from useChat...');
                // handleSubmit already prevents default, so just call it
                handleSubmit(e);
              }} 
              className="p-4 border-t border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 rounded-lg border border-white/10 bg-black/50 px-3 py-2">
                  <span className="text-[#AEA36D] font-mono font-bold select-none">❯</span>
                  <input
                    value={input}
                    onChange={handleInputChange}
                    placeholder="type command or question..."
                    className="flex-1 bg-transparent font-mono text-sm text-[#AEA36D] placeholder-white/30 focus:outline-none"
                    disabled={isLoading}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        console.log('[Chat] Enter key pressed, submitting...');
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowSuggestionPanel(true)}
                  className="rounded-2xl border border-white/10 px-3 py-2 text-white/60 hover:text-[#AEA36D] hover:border-[#AEA36D]/30 transition-colors"
                  title="Submit feature suggestion"
                >
                  <Lightbulb size={18} />
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className="rounded-2xl bg-gradient-to-br from-[#F6E7AB] to-[#9E7B2F] px-4 py-2 text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>

              {/* Feature Suggestion Panel */}
              <FeatureSuggestionPanel
                isOpen={showSuggestionPanel}
                onClose={() => setShowSuggestionPanel(false)}
                user={user}
                prefillTitle={suggestionPrefill.title}
                prefillDescription={suggestionPrefill.description}
                prefillType={suggestionPrefill.type}
              />
            </form>
          </motion.div>
        </MotionDiv>
      )}
    </AnimatePresence>
  );
};

const StatusBadge = ({ connection }) => {
  const visual = STATUS_VISUALS[connection.state] || STATUS_VISUALS.ready;
  return (
    <div className={`flex items-center gap-1.5 text-[10px] ${visual.textClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${visual.dotClass}`} />
      <span>{visual.label}</span>
    </div>
  );
};

export default AgentChatBubble;
